import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BimloaFilter.module.scss';
import classes from 'classnames';
import Icon from '../Icon/Icon';
import DropDown, { Option } from '../DropDown/DropDown';
import DatePicker from './DatePicker';

export type FilterField = {
  key: string;
  label: string;
  options: Option[];
};

type BimloaFilterProps = {
  fields: FilterField[];
  values: Record<string, Option>;
  onChange: (values: Record<string, Option>) => void;
  isOpen?: boolean;
  style?: React.CSSProperties;
};

export const BimloaFilter: React.FC<BimloaFilterProps> = ({
  fields,
  values,
  onChange,
  isOpen = true,
  style,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(!isOpen || false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const active = fields
    .filter(f => values[f.key] && values[f.key].value !== 'All')
    .map(f => f.label)
    .join(', ');
  const buttonLabel = active ? `${active}` : '-';

  useEffect(() => {
    if (open) {
      const handleOutsideClick = (event: any) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }
  }, [open]);

  const handleResetAll = () => {
    onChange({});
  };

  return (
    <div className={styles.bimloaFilter}>
      <button
        className={classes(styles.filterByButton, {
          [styles.open]: open,
        })}
        onClick={() => setOpen(true)}
      >
        <Icon icon='filter' />
        <span>{t('genericTable.bimloaFilter.filterBy')}</span>
        <span className={styles.activeLabel}>{buttonLabel}</span>
      </button>

      {open && (
        <div className={styles.overlay} style={style} ref={containerRef}>
          <div className={styles.filterHeader}>
            <h3 className={styles.title}>{t('genericTable.bimloaFilter.filter')}</h3>
            <button
              className={styles.close}
              onClick={() => setOpen(false)}
              aria-label={t('genericTable.bimloaFilter.close') || undefined}
            >
              <Icon icon='x' width={20} height={20} />
            </button>
          </div>

          <div className={styles.fields}>
            {[...fields].reverse().map(f => (
              <div key={f.key} className={styles.filterRow}>
                <div className={styles.filterRowHeader}>
                  <label className={styles.label}>{f.label}</label>
                  <button
                    className={styles.resetBtn}
                    onClick={() => {
                      const newValues = { ...values };

                      if (values[f.key]?.data?.type === 'date') {
                        if (values[f.key]?.data?.date) {
                          delete values[f.key].data.date;
                        }
                      }

                      if (values[f.key]?.data?.type === 'date_range') {
                        if (values[f.key]?.data?.fromDate) {
                          delete values[f.key].data.fromDate;
                        }
                        if (values[f.key]?.data?.toDate) {
                          delete values[f.key].data.toDate;
                        }
                      }

                      delete newValues[f.key];
                      onChange(newValues);
                    }}
                    disabled={!values[f.key] || values[f.key].value === 'All'}
                  >
                    {t('genericTable.bimloaFilter.reset')}
                  </button>
                </div>
                <DropDown
                  options={f.options}
                  value={values[f.key]}
                  onChange={opt => {
                    onChange({ ...values, [f.key]: opt });
                  }}
                  placeholder={t('genericTable.bimloaFilter.all') || undefined}
                />
                {values[f.key]?.data?.type === 'date' && (
                  <div>
                    <div className={styles.filterRowHeader} style={{ marginTop: '10px' }}>
                      <label className={styles.label}>
                        {t('genericTable.bimloaFilter.chooseDate')}
                      </label>
                    </div>
                    <div>
                      <DatePicker
                        date={values[f.key].data.date}
                        setDate={date => {
                          const opt = values[f.key];
                          opt.data.date = date;
                          onChange({ ...values, [f.key]: opt });
                        }}
                      />
                    </div>
                  </div>
                )}
                {values[f.key]?.data?.type === 'date_range' && (
                  <div>
                    <div className={styles.filterRowHeader} style={{ marginTop: '10px' }}>
                      <label className={styles.label}>
                        {t('genericTable.bimloaFilter.chooseDateRange')}
                      </label>
                    </div>
                    <div className={styles.dateRangeSelector}>
                      <div>
                        <div className={styles.datePickerLabel}>
                          {t('genericTable.bimloaFilter.from')}
                        </div>
                        <DatePicker
                          className={styles.datePickerLeftBoundary}
                          date={values[f.key].data.fromDate}
                          setDate={date => {
                            const opt = values[f.key];
                            opt.data.fromDate = date;
                            onChange({ ...values, [f.key]: opt });
                          }}
                          selectsStart={true}
                          startDate={values[f.key].data.fromDate}
                          endDate={values[f.key].data.toDate}
                          maxDate={values[f.key].data.toDate}
                        />
                      </div>
                      <div>
                        <div className={styles.datePickerLabel}>
                          {t('genericTable.bimloaFilter.to')}
                        </div>
                        <DatePicker
                          className={styles.datePickerRightBoundary}
                          date={values[f.key].data.toDate}
                          setDate={date => {
                            const opt = values[f.key];
                            opt.data.toDate = date;
                            onChange({ ...values, [f.key]: opt });
                          }}
                          selectsEnd={true}
                          startDate={values[f.key].data.fromDate}
                          endDate={values[f.key].data.toDate}
                          minDate={values[f.key].data.fromDate}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className={styles.resetAllBtn} onClick={handleResetAll}>
            {t('genericTable.bimloaFilter.resetAll')}
          </button>
        </div>
      )}
    </div>
  );
};
